// src/pages/quotation/QuotationDetailsPage.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ChevronLeft, FileText, RefreshCw, XCircle, User, Package, Printer, Store, CalendarClock, ShoppingBag, Phone, Calendar } from 'lucide-react';
import { PageHeader, PrintPreviewModal, InfoCard } from '../../components/common';
import { getQuotation, convertQuotation } from '../../services/api/offlineSales/quotations';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { usePermissions } from '../../hooks/usePermissions';
import { formatDate, formatDateTime } from '../../utils/dateUtils';
import { formatCurrency } from '../../utils/orderCalculations';
import { getActiveSession } from '../../services/api/offlineSales/cashierSessions';
import { exportQuotationPDF } from '../../utils/offlineOrderPdfExporter';
import { getCssVar } from '../../utils/themeColors';
import Dropdown from '../../components/ui/Dropdown/Dropdown';
import QuotationA4Document from './QuotationA4Document';
import ProductThumb from '../procurement/components/ProductThumb';
import ps from '../procurement/components/ProcurementDetails.module.css';
import styles from './QuotationDetailsPage.module.css';
import receiptStyles from '../offlineSales/components/OrderReceiptPrintModal.module.css';

const STATUS_COLORS = {
  draft: 'default',
  sent: 'info',
  accepted: 'success',
  rejected: 'error',
  expired: 'warning',
  converted: 'success',
};

const Badge = ({ type, label }) => (
  <span className={`${styles.badge} ${styles[`badge_${type}`]}`}>{label}</span>
);

// ─── Convert to Order Modal ─────────────────────────────────────────────────
const ConvertModal = ({ onConfirm, onClose, loading }) => {
  const { t } = useLocalization();
  const [fulfillmentMethod, setFulfillmentMethod] = useState('counter_pickup');
  const [orderStatusKey, setOrderStatusKey] = useState('offline_credit');
  const [immediateDelivery, setImmediateDelivery] = useState(false);

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.smallModal} onClick={e => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>{t('quotation.convertToOrder')}</h3>
          <button onClick={onClose} className={styles.iconBtn}><XCircle size={16} /></button>
        </div>
        <div className={styles.modalBody}>
          <label>{t('quotation.fulfillmentMethod')}</label>
          <Dropdown
            value={fulfillmentMethod}
            onChange={setFulfillmentMethod}
            options={[
              { value: 'counter_pickup', label: t('quotation.fulfillmentCounterPickup') },
              { value: 'self_delivery', label: t('quotation.fulfillmentSelfDelivery') },
              { value: 'courier', label: t('quotation.fulfillmentCourier') },
            ]}
            allowClear={false}
            className={styles.select}
          />

          <label style={{ marginTop: '0.75rem', display: 'block' }}>{t('quotation.orderStatus')}</label>
          <Dropdown
            value={orderStatusKey}
            onChange={setOrderStatusKey}
            options={[
              { value: 'offline_credit', label: t('quotation.orderStatusCredit') },
              { value: 'offline_completed', label: t('quotation.orderStatusCompleted') },
            ]}
            allowClear={false}
            className={styles.select}
          />

          <label className={styles.checkboxRow}>
            <input
              type="checkbox"
              checked={immediateDelivery}
              onChange={e => setImmediateDelivery(e.target.checked)}
            />
            {t('quotation.immediateDelivery')}
          </label>
        </div>
        <div className={styles.modalFooter}>
          <button onClick={onClose} className={styles.cancelBtn}>{t('common.cancel')}</button>
          <button
            disabled={loading}
            onClick={() => onConfirm({ fulfillmentMethod, orderStatusKey, immediateDelivery })}
            className={styles.primaryBtn}
          >
            {loading ? '...' : t('quotation.confirmConvert')}
          </button>
        </div>
      </div>
    </div>
  );
};

const QuotationDetailsPage = () => {
  const { quotationId } = useParams();
  const navigate = useNavigate();
  const { t } = useLocalization();
  const { success, error } = useAlert();
  const { hasPermission } = usePermissions();

  const [quotation, setQuotation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showConvert, setShowConvert] = useState(false);
  const [convertLoading, setConvertLoading] = useState(false);
  const [activeSession, setActiveSession] = useState(null);
  const [sessionLoading, setSessionLoading] = useState(false);
  const [showPrint, setShowPrint] = useState(false);

  const loadQuotation = async () => {
    try {
      setLoading(true);
      const result = await getQuotation(quotationId);
      setQuotation(result);
    } catch (err) {
      error(err?.response?.data?.message || t('quotation.failedToLoadQuotation'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadQuotation(); }, [quotationId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!quotation?.shopId) return undefined;
    let cancelled = false;
    setSessionLoading(true);
    getActiveSession(quotation.shopId)
      .then(result => {
        if (!cancelled) setActiveSession(result?.session || null);
      })
      .catch(() => {
        if (!cancelled) setActiveSession(null);
      })
      .finally(() => {
        if (!cancelled) setSessionLoading(false);
      });
    return () => { cancelled = true; };
  }, [quotation?.shopId]);

  const handleConvert = async (payload) => {
    if (sessionLoading) return;
    if (quotation?.Shop?.cashierSessionRequired && !activeSession) {
      error(t('offlineSales.sessionNotFound'));
      return;
    }
    try {
      setConvertLoading(true);
      const result = await convertQuotation(quotationId, {
        ...payload,
        sessionId: activeSession?.id || null,
      });
      success(t('quotation.convertedSuccessfully'));
      setShowConvert(false);
      if (result?.order?.id) {
        navigate(`/offline-sales/orders/${result.order.id}`);
      } else {
        loadQuotation();
      }
    } catch (err) {
      error(err?.response?.data?.message || t('quotation.failedToConvert'));
    } finally {
      setConvertLoading(false);
    }
  };

  const handlePrint = () => setShowPrint(true);

  const renderReceipt = () => (
    <div className={receiptStyles.receiptArea}>
      <h2 className={receiptStyles.receiptTitle}>{t('quotation.quotationNumber')}</h2>
      {quotation.Shop?.name && <p className={receiptStyles.receiptShop}>{quotation.Shop.name}</p>}
      <div className={receiptStyles.receiptMeta}>
        <div className={receiptStyles.receiptMetaRow}>
          <span>#</span>
          <span>{quotation.quotationNumber}</span>
        </div>
        <div className={receiptStyles.receiptMetaRow}>
          <span>{t('common.created')}</span>
          <span>{new Date(quotation.createdAt).toLocaleString()}</span>
        </div>
        <div className={receiptStyles.receiptMetaRow}>
          <span>{t('quotation.customer')}</span>
          <span>{quotation.customer?.name || quotation.prospectName || t('common.na')}</span>
        </div>
      </div>
      <hr className={receiptStyles.receiptDivider} />
      <div className={receiptStyles.receiptItems}>
        {(quotation.items || []).map((item) => (
          <div key={item.id} className={receiptStyles.receiptItem}>
            <span className={receiptStyles.receiptItemName}>{item.Product?.name || item.displayName}</span>
            <span className={receiptStyles.receiptItemQty}>×{item.quantity}</span>
            <span className={receiptStyles.receiptItemTotal}>{formatCurrency(item.lineTotal)}</span>
          </div>
        ))}
      </div>
      <hr className={receiptStyles.receiptDivider} />
      <div className={`${receiptStyles.receiptTotalRow} ${receiptStyles.receiptGrandTotal}`}>
        <strong>{t('quotation.total')}</strong>
        <strong>{formatCurrency(itemsTotal)}</strong>
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
      </div>
    );
  }

  if (!quotation) {
    return <div className={styles.notFound}>{t('errors.notFound')}</div>;
  }

  const canConvert = ['draft', 'sent', 'accepted'].includes(quotation.status) && hasPermission('convert_quotation');

  const itemsTotal = (quotation.items || []).reduce((sum, item) => sum + parseFloat(item.lineTotal || 0), 0);

  const renderContent = () => (
    <div className={styles.grid}>
      <div className={styles.column}>
        <InfoCard icon={FileText} title={t('quotation.quotationNumber')}>
          <div className={ps.infoGrid}>
            <div className={ps.infoItem}>
              <span className={ps.infoLabel}>{t('common.status')}</span>
              <span className={ps.infoValue}>
                <Badge type={STATUS_COLORS[quotation.status]} label={t(`quotation.status_${quotation.status}`)} />
              </span>
            </div>
            <div className={ps.infoItem}>
              <span className={ps.infoLabel}>{t('quotation.shop')}</span>
              <span className={ps.infoValue}><Store size={14} />{quotation.Shop?.name || t('common.na')}</span>
            </div>
            <div className={ps.infoItem}>
              <span className={ps.infoLabel}>{t('quotation.validUntil')}</span>
              <span className={ps.infoValue}><CalendarClock size={14} />{quotation.validUntil ? formatDate(quotation.validUntil) : t('common.na')}</span>
            </div>
            {quotation.convertedOrderId && (
              <div className={ps.infoItem}>
                <span className={ps.infoLabel}>{t('quotation.convertedToOrder')}</span>
                <span className={ps.infoValue}>
                  <ShoppingBag size={14} />
                  <Link to={`/offline-sales/orders/${quotation.convertedOrderId}`}>{quotation.convertedOrder?.orderNumber || `#${quotation.convertedOrderId}`}</Link>
                </span>
              </div>
            )}
            <div className={ps.divider} />
            <div className={`${ps.infoItem} ${ps.fullWidth}`}>
              <span className={ps.infoLabel}>{t('quotation.total')}</span>
              <span className={`${ps.infoValue} ${ps.infoValueLarge}`}>{formatCurrency(itemsTotal)}</span>
            </div>
          </div>
        </InfoCard>

        <InfoCard icon={User} title={t('quotation.customer')}>
          <div className={ps.infoGrid}>
            <div className={ps.infoItem}>
              <span className={ps.infoLabel}>{t('quotation.customerName')}</span>
              <span className={ps.infoValue}><User size={14} />{quotation.customer?.name || quotation.prospectName || t('common.na')}</span>
            </div>
            <div className={ps.infoItem}>
              <span className={ps.infoLabel}>{t('quotation.customerPhone')}</span>
              <span className={ps.infoValue}><Phone size={14} />{quotation.customer?.phone || quotation.prospectPhone || t('common.na')}</span>
            </div>
            {quotation.notes && (
              <div className={`${ps.infoItem} ${ps.fullWidth}`}>
                <span className={ps.infoLabel}>{t('quotation.notes')}</span>
                <p className={ps.notesText}>{quotation.notes}</p>
              </div>
            )}
          </div>
        </InfoCard>
      </div>

      <div className={styles.column}>
      <InfoCard icon={Package} title={t('quotation.items')}>
        <div className={ps.tableWrapper}>
          <table className={ps.itemsTable}>
            <thead>
              <tr>
                <th style={{ width: '40px' }}>#</th>
                <th>{t('quotation.items')}</th>
                <th className={ps.alignRight}>{t('quotation.unitPrice')}</th>
                <th className={ps.alignRight}>{t('quotation.quantity')}</th>
                <th className={ps.alignRight}>{t('quotation.lineDiscount')}</th>
                <th className={ps.alignRight}>{t('quotation.lineTotal')}</th>
              </tr>
            </thead>
            <tbody>
              {(quotation.items || []).map((item, index) => (
                <tr key={item.id}>
                  <td className={ps.itemIndexCell}>{index + 1}</td>
                  <td>
                    <div className={ps.productCellInner}>
                      <ProductThumb item={item} alt={item.Product?.name || ''} />
                      <div className={ps.productCell}>
                        <span className={ps.productName}>{item.Product?.name || item.displayName || t('common.na')}</span>
                      </div>
                    </div>
                  </td>
                  <td className={`${ps.alignRight} ${ps.amountCell}`}>{formatCurrency(item.unitPrice)}</td>
                  <td className={`${ps.alignRight} ${ps.amountCell}`}>{item.quantity}</td>
                  <td className={`${ps.alignRight} ${ps.amountCell}`}>{formatCurrency(item.lineDiscount || 0)}</td>
                  <td className={`${ps.alignRight} ${ps.amountCell} ${ps.amountStrong}`}>{formatCurrency(item.lineTotal)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td colSpan={5} className={ps.alignRight}>{t('quotation.total')}</td>
                <td className={`${ps.alignRight} ${ps.amountCell}`}>{formatCurrency(itemsTotal)}</td>
              </tr>
            </tfoot>
          </table>
        </div>
      </InfoCard>

      <InfoCard icon={FileText} title={t('ecomOrders.additionalInformation')}>
        <div className={ps.infoList}>
          <div className={ps.infoListItem}>
            <label>{t('common.created')}</label>
            <p><Calendar size={14} />{formatDateTime(quotation.createdAt)}</p>
          </div>
        </div>
      </InfoCard>
      </div>
    </div>
  );

  return (
    <div className={styles.page}>
      <PageHeader
        title={quotation.quotationNumber}
        icon={null}
        breadcrumbs={
          <button onClick={() => navigate('/offline-sales/quotations')} className={styles.backBtn}>
            <ChevronLeft size={16} /> {t('quotation.allQuotations')}
          </button>
        }
        actions={
          <div className={styles.headerActions}>
            <button className={styles.secondaryBtn} onClick={handlePrint}>
              <Printer size={15} /> {t('common.printReceipt', 'Print Receipt')}
            </button>
            {canConvert && (
              <button className={styles.primaryBtn} onClick={() => setShowConvert(true)}>
                <RefreshCw size={15} /> {t('quotation.convertToOrder')}
              </button>
            )}
          </div>
        }
      />

      {renderContent()}

      {showConvert && (
        <ConvertModal
          onConfirm={handleConvert}
          onClose={() => setShowConvert(false)}
          loading={convertLoading}
        />
      )}

      {showPrint && (
        <PrintPreviewModal
          title={`Quotation — #${quotation.quotationNumber}`}
          onClose={() => setShowPrint(false)}
          pdfFileName={`Quotation_${quotation.quotationNumber || quotation.id}.pdf`}
          onDownloadPdf={() => exportQuotationPDF(quotation, {
            primary: getCssVar('--color-primary', '#4f46e5'),
            primaryDark: getCssVar('--color-primary-dark', '#3730a3'),
          })}
          renderReceipt={renderReceipt}
          renderA4={() => <QuotationA4Document quotation={quotation} />}
        />
      )}
    </div>
  );
};

export default QuotationDetailsPage;
